<template>
  <Teleport to="body">
    <div class="new-modal" @click.self="$emit('close')">
      <section
        v-bind="$attrs"
        ref="dialog"
        class="new-modal__dialog"
        :class="size === 'compact' ? 'new-modal__dialog--compact' : ''"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        tabindex="-1"
      >
        <button class="new-modal__close" type="button" aria-label="Close modal" @click="$emit('close')">
          <img src="/new-studio/icons/modal-close.svg" alt="">
        </button>
        <h2 :id="titleId">{{ title }}</h2>
        <slot />
      </section>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
defineOptions({ inheritAttrs: false })
withDefaults(defineProps<{ title: string; size?: 'standard' | 'compact' }>(), { size: 'standard' })
const emit = defineEmits<{ close: [] }>()
const dialog = useTemplateRef<HTMLElement>('dialog')
const titleId = `new-studio-modal-${useId()}`
const previousFocus = ref<HTMLElement | null>(null)
const focusableElements = () => Array.from(dialog.value?.querySelectorAll<HTMLElement>(
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
) || []).filter(element => !element.hasAttribute('hidden') && element.getClientRects().length > 0)
const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') emit('close')
  if (event.key === 'Tab') {
    const focusable = focusableElements()
    if (!focusable.length) {
      event.preventDefault()
      dialog.value?.focus()
      return
    }
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.value)) {
      event.preventDefault()
      last.focus()
    }
    else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }
}
const onFocusIn = (event: FocusEvent) => {
  if (!dialog.value || dialog.value.contains(event.target as Node)) return
  const [first] = focusableElements()
  ;(first || dialog.value).focus()
}
onMounted(() => {
  previousFocus.value = document.activeElement as HTMLElement
  window.addEventListener('keydown', onKeydown)
  document.addEventListener('focusin', onFocusIn)
  nextTick(() => dialog.value?.focus())
})
onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
  document.removeEventListener('focusin', onFocusIn)
  previousFocus.value?.focus()
})
</script>

<style scoped>
.new-modal { position: fixed; z-index: 9000; inset: 0; display: grid; padding: 42px; place-items: center; overflow-y: auto; background: rgb(28 28 30 / 28%); backdrop-filter: blur(13px); }
.new-modal__dialog { position: relative; width: min(788px, calc(100vw - 84px)); max-height: calc(100vh - 84px); padding: 40px; overflow-y: auto; background: #fff; box-shadow: 0 21.54px 31.412px rgb(20 20 22 / 34%); color: #1c1c1e; }
.new-modal__dialog--compact { width: min(522px, calc(100vw - 84px)); }
.new-modal__dialog:focus, .new-modal__dialog:focus-visible { outline: none !important; }
.new-modal__close { position: absolute; z-index: 2; top: 40px; right: 38px; display: grid; width: 20px; height: 20px; padding: 0; place-items: center; border: 0; background: transparent; color: #1c1c1e; font-size: 16px; cursor: pointer; }
.new-modal__close img { width: 11.682px; height: 11.682px; }
h2 { margin: 0; text-align: center; font-size: 38px; font-weight: 800; line-height: normal; letter-spacing: -.38px; }
@media (prefers-reduced-motion: no-preference) { .new-modal__dialog { animation: modal-in 180ms cubic-bezier(.22,1,.36,1); } }
@keyframes modal-in { from { transform: translateY(10px); opacity: 0; } }
</style>
